import Feature from "ol/Feature";
import {L} from "../../../lt-ng/landcare-toolkit";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import {LTServices} from "../../../lt-ng/lt-services";
import {hoverStyleFn, selectedStyleFn} from "../../features-api/features-layer";
import {_t} from "../../../utility/i18n";
import {StyleFunction} from "ol/style/Style";
import {decode} from "@manaakiwhenua/ol-style-editor/src";
import {normalStyles} from "../styles/normal";
import {generateLegendItems} from "./legend-images";
import {GeoAppEvents} from "../../../controllers/events/geoapp_events";
import Item = L.tree.Item;
import IMapService = LTServices.IMapService;
import {Geometry} from "ol/geom";
import {Type} from "ol/geom/Geometry";
import {ILayerTreeManipulationService} from "../../oe/layer-tree-manipulation";
import {IFeatureState} from "../../feature_state";

export interface ILocalDataLayers {
    rootNode: Item;
    addLayer(file: File, features: Feature<Geometry>[]);
}

export class LocalDataLayers implements ILocalDataLayers {
    public static $inject = ['landcareMapService', '$rootScope', 'layerTreeManipulationService', 'featureState'];

    rootNode: Item = {
        name: _t("LocalDataLayers.GROUP_NAME", "Local data"),
        isChecked: L.tree.isChecked,
        children: [],
        layerSelectorChildren: [],
        checked: false,
        expanded: true,
        extraState: {
            metaLayerSelector: {
                checked: true,
                expanded: false
            }
        },
        click: L.tree.clickItem,
        index: 1999,
        id: 1999,
        rootItems: [],
        checkBehavior: "UNION"
    }

    constructor(protected mapService: IMapService,
                protected $rootScope: ng.IRootScopeService,
                protected layerTreeManipulationService: ILayerTreeManipulationService,
                protected featureStatue: IFeatureState) {
        mapService.mapPromise.then(() => {
            mapService.treeData.unshift(this.rootNode);
            mapService.addLayer(this.rootNode);
        });
    }

    private createStyle(styleDefinition): StyleFunction {
        const olStyle = decode(styleDefinition);

        // node.children = generateLegendItems(source, styleDefinition, id+1, node);

        return (f, resolution) => {
            const geoType = f.getGeometry().getType();
            if (!['Point', 'MultiPoint'].includes(geoType)) {
                if (f.get('hover')) {
                    return hoverStyleFn(f, resolution);
                }

                if (f.get('selected')) {
                    return selectedStyleFn(f, resolution)
                }
            }

            return olStyle(f, resolution);
        }
    }

    addLayer(file: File, features: Feature<Geometry>[]): ng.IPromise<void> {
        const source = new VectorSource({
            features
        });

        const self = this;

        return this.mapService.nextId().then(id => {
            const notAllStyled = !features.every(f => f.getStyle() || f.getStyleFunction());

            let styleDefinition = notAllStyled ? normalStyles() : undefined;
            let olStyle: StyleFunction = notAllStyled ? this.createStyle(styleDefinition) : undefined;

            let layer = new VectorLayer({
                source,
                visible: false,
                style: olStyle,
                zIndex: L.util.Layer.zIndexForLayerType("CONTEXT", 2000)
            });

            const queryableTypes: Type[] = [
                'Point',
                'MultiPoint',
                'Polygon',
                'MultiPolygon',
            ];

            const node: Item = {
                id,
                index: id,
                name: file.name,
                layer,
                layerService: {
                    id,
                    description: file.name,
                    queryable: source.getFeatures().some(f => queryableTypes.includes(f.getGeometry().getType()))
                } as any,
                children: [],
                parent: this.rootNode,
                isLocalData: true,
                isChecked: L.tree.isChecked,
                checkBehavior: "UNION",
                click(node) {
                    console.log('LocalDataLayer: click local data node', node);
                    L.tree.clickItem(node);
                },
                extraState: {
                    metaLayerSelector: {
                        checked: true,
                        expanded: false
                    }
                },
                rootItems: [],
                get styleDefinition() {
                    return styleDefinition;
                },
                set styleDefinition(newStyle) {
                    styleDefinition = newStyle;
                    olStyle = self.createStyle(styleDefinition);
                    layer.setStyle(olStyle)
                    node.children = generateLegendItems(source, styleDefinition, id+1, node);
                    self.$rootScope.$broadcast(GeoAppEvents.showLegendAndMetadata, node);
                },
            };

            if (styleDefinition) {
                node.children.push(...generateLegendItems(source, styleDefinition, id+1, node))
            }


            this.rootNode.children.push(node);
            this.rootNode.layerSelectorChildren.push(node);
            this.rootNode.extraState.metaLayerSelector.checked = true;
            this.rootNode.extraState.metaLayerSelector.expanded = true;

            return this.mapService.addLayer(node)
                .then(() => {
                    this.layerTreeManipulationService.toggleLayer(node, true);
                    this.featureStatue.toggleMode('customLayer', node);
                })
        });
    }
}